import BusinessIcon from '@mui/icons-material/Business';
import CalculateIcon from '@mui/icons-material/Calculate';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { Stack } from '@mui/material';
import { getRecieverFactoringRequestOverview } from 'lib/api/factoring';
import { formatDateString, formatMoney } from 'lib/functions/utils';
import {
  ContentCard,
  ErrorState,
  KeyValueCard,
  StatNumber,
  TabContentSkeleton,
} from 'lib/mobileDesignSystem';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useFactoringOperationDetail } from '../context';

export interface OverviewProps {
  requestId: string;
}

const Overview: React.FC<OverviewProps> = ({ requestId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setOperationStatus, setQuoteId } = useFactoringOperationDetail();
  const {
    data: operation,
    isLoading,
    isError,
  } = getRecieverFactoringRequestOverview(dispatch, { requestId });

  useEffect(() => {
    if (operation?.status != null) {
      setOperationStatus(operation.status);
    }
  }, [operation?.status, setOperationStatus]);

  useEffect(() => {
    setQuoteId(operation?.quoteId ?? null);
  }, [operation?.quoteId, setQuoteId]);

  const simulationItems = useMemo(() => {
    if (!operation) return [];
    return [
      {
        label: 'Monto operación',
        value: `${formatMoney(operation.amount)} CLP`,
      },
      {
        label: `Monto a financiar (${operation.advance}%)`,
        value: `${formatMoney(operation.operationAdvanced)} CLP`,
      },
      {
        label: `Tasa del fondo (${operation.fundRate}%)`,
        value: `${formatMoney(operation.operationCostFundRate)} CLP`,
      },
      {
        label: `Spread (${operation.spread}%)`,
        value: `${formatMoney(operation.operationCostSpread)} CLP`,
      },
      {
        label: 'Documentos aplicados',
        value: `${formatMoney(operation.amountToApply)} CLP`,
      },
      {
        label: `Otros gastos${operation.amountIVA > 0 ? ' + IVA' : ''}`,
        value: `${formatMoney(operation.amountIVA + operation.expensesCost)} CLP`,
      },
      {
        label: 'Comisión operación + IVA',
        value: `${formatMoney(
          operation.operationCostComissionFixed +
            operation.operationCostComissionVariable +
            operation.operationIva,
        )} CLP`,
      },
      {
        label: 'Monto a girar',
        value: `${formatMoney(operation.amountToDepositTotal)} CLP`,
      },
    ];
  }, [operation]);

  if (isLoading) {
    return <TabContentSkeleton />;
  }

  if (isError) {
    return <ErrorState message="Ocurrió un error al cargar el detalle." />;
  }

  if (!operation) {
    return <ErrorState message="No se encontró la operación." />;
  }

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <ContentCard
        header={{
          icon: <BusinessIcon fontSize="small" />,
          title: operation.businessName,
          description: `${operation.businessIdentifier}`,
        }}
      >
        <Stack spacing={1} direction="row">
          <StatNumber label="Anticipo" amount={operation.advance} tag="%" />
          <StatNumber label="Interés" amount={operation.rate} tag="%" />
          <StatNumber label="Fondo" amount={operation.fundRate} tag="%" />
          <StatNumber label="Mora" amount={operation.arrearRate} tag="%" />
        </Stack>
      </ContentCard>
      <ContentCard
        header={{
          icon: <CalculateIcon sx={{ fontSize: 20 }} />,
          title: 'Detalles de simulación',
          description: formatDateString(operation.requestDate, 'dd-mm-yyyy'),
        }}
      >
        <KeyValueCard items={simulationItems} highlightLastRow />
      </ContentCard>
      <ContentCard
        header={{
          icon: <FilePresentIcon sx={{ fontSize: 20 }} />,
          title: 'Documentos',
          description: `${operation.documents.length} documento${operation.documents.length !== 1 ? 's' : ''}`,
          clickable: true,
          onClick: () => navigate(`/finance/factoring/${requestId}/documents`),
        }}
      />
    </Stack>
  );
};

export default Overview;
